import { useState, useEffect } from "react";
import { Plus, Trash2, Zap, ListPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { detectCptCode } from "@/lib/cptLookup";
import { formatNumber } from "@/lib/constants";
import { addToQuickAddCptList } from "@/lib/api";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";

// Context Menu for entries
function EntryContextMenu({ x, y, entry, onClose, onAddToQuickAdd }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  useEffect(() => {
    const handleClick = () => onClose();
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div 
      className={`fixed z-[200] border rounded-lg shadow-xl py-1 min-w-[200px] ${
        isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
      }`}
      style={{ left: x, top: y }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={`px-3 py-2 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
        <p className={`text-xs truncate max-w-[180px] ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
          {entry.label}
        </p>
        <p className={`text-xs font-mono mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
          {entry.wrvus} wRVU
        </p>
      </div>
      <button
        onClick={() => { onAddToQuickAdd(entry); onClose(); }}
        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left ${
          isDark 
            ? 'text-slate-200 hover:bg-blue-500/20 hover:text-blue-300'
            : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
        }`}
      >
        <ListPlus size={14} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
        Add to Quick-Add CPT List
      </button>
    </div>
  );
}

export default function MiscEntries({ entries, onAdd, onRemove, onUpdate, onCptListUpdated }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [matchedCodes, setMatchedCodes] = useState({});
  const [contextMenu, setContextMenu] = useState(null);

  // Extract CPT code from label
  const extractCptCode = (label) => {
    const match = label.match(/(\d{5})/);
    return match ? match[1] : null;
  };

  const handleContextMenu = (e, entry) => {
    e.preventDefault();
    const code = extractCptCode(entry.label);
    if (code) {
      setContextMenu({ x: e.clientX, y: e.clientY, entry });
    }
  };

  const handleAddToQuickAdd = async (entry) => {
    const code = extractCptCode(entry.label);
    if (!code) {
      toast.error("Could not extract CPT code from entry");
      return;
    }
    const description = entry.label.replace(/^\d{5}\s*[—-]?\s*/, "").trim();
    try {
      await addToQuickAddCptList(code, description, parseFloat(entry.wrvus) || 0);
      toast.success(`Added ${code} to Quick-Add CPT List`);
      // Trigger refresh of the CPT panel
      if (onCptListUpdated) onCptListUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add to Quick-Add CPT List");
    }
  };

  const handleLabelChange = (id, value) => {
    onUpdate(id, "label", value);

    const result = detectCptCode(value);
    if (result) {
      // Auto-fill the label with CPT name and set wRVU
      onUpdate(id, "label", `${result.code} — ${result.name}`);
      onUpdate(id, "wrvus", String(result.wrvu));
      setMatchedCodes(prev => ({ ...prev, [id]: result.code }));
    } else {
      setMatchedCodes(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <div data-testid="misc-entries-section" className={`rounded-xl border-2 p-6 mb-8 ${
      isDark 
        ? 'border-purple-500/20 bg-purple-500/5'
        : 'border-purple-200 bg-purple-50/30'
    }`}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className={`text-2xl md:text-3xl font-bold ${
            isDark ? 'text-purple-300' : 'text-purple-700'
          }`} style={{ fontFamily: "'Manrope', sans-serif" }}>
            Other Patient Visits
          </h3>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
            Type a 5-digit CPT code to auto-fill name and wRVUs
          </p>
        </div>
        <Button
          data-testid="add-misc-entry-btn"
          onClick={onAdd}
          className={`border transition-colors h-12 px-5 text-base font-semibold ${
            isDark 
              ? 'bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 border-purple-500/30 hover:border-purple-400/50'
              : 'bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-300 hover:border-purple-500'
          }`}
        >
          <Plus size={20} className="mr-2" />
          Add Entry
        </Button>
      </div>

      {entries.length === 0 && (
        <p data-testid="misc-empty" className={`text-base italic py-6 text-center ${
          isDark ? 'text-slate-600' : 'text-gray-500'
        }`}>
          No miscellaneous entries yet — click "Add Entry" to log one
        </p>
      )}

      <div className="space-y-4">
        {entries.map((entry, index) => (
          <div key={entry.id}>
            <div
              data-testid={`misc-entry-${index}`}
              className="flex items-center gap-4 animate-slide-up"
              style={{ animationDelay: '0s' }}
              onContextMenu={(e) => handleContextMenu(e, entry)}
            >
              <div className="flex-1 relative">
                <Input
                  data-testid={`misc-label-${index}`}
                  placeholder="Description or CPT code..."
                  value={entry.label}
                  onChange={(e) => handleLabelChange(entry.id, e.target.value)}
                  className={`border text-base h-12 ${
                    isDark 
                      ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-purple-500/50 text-slate-200 placeholder:text-slate-600'
                      : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-purple-500 text-gray-900 placeholder:text-gray-400'
                  }`}
                />
              </div>
              <div className="w-44">
                <div className="relative">
                  <Input
                    data-testid={`misc-wrvu-${index}`}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={entry.wrvus || ""}
                    onChange={(e) => onUpdate(entry.id, "wrvus", e.target.value)}
                    className={`border font-mono text-base h-12 pr-16 ${
                      isDark 
                        ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-purple-500/50 text-purple-300 placeholder:text-slate-600'
                        : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-purple-500 text-purple-700 placeholder:text-gray-400'
                    }`}
                  />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm font-mono ${
                    isDark ? 'text-slate-500' : 'text-gray-500'
                  }`}>wRVU</span>
                </div>
              </div>
              <Button
                data-testid={`misc-delete-${index}`}
                size="icon"
                variant="ghost"
                onClick={() => onRemove(entry.id)}
                className={`h-12 w-12 flex-shrink-0 ${
                  isDark 
                    ? 'text-slate-600 hover:text-red-400 hover:bg-red-500/10'
                    : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                }`}
              >
                <Trash2 size={18} />
              </Button>
            </div>
            {matchedCodes[entry.id] && (
              <div className="flex items-center gap-1.5 mt-1.5 ml-1">
                <Zap size={12} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
                <span className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  Auto-filled from CPT {matchedCodes[entry.id]}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {entries.length > 0 && (
        <div className={`mt-5 pt-4 border-t flex items-center justify-between ${
          isDark ? 'border-purple-500/10' : 'border-purple-200'
        }`}>
          <span className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
            {entries.length} misc patient{entries.length !== 1 ? 's' : ''}
          </span>
          <span className={`text-base font-mono font-bold ${
            isDark ? 'text-purple-300' : 'text-purple-700'
          }`}>
            {formatNumber(entries.reduce((sum, e) => sum + (parseFloat(e.wrvus) || 0), 0))} wRVU
          </span>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <EntryContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          entry={contextMenu.entry}
          onClose={() => setContextMenu(null)}
          onAddToQuickAdd={handleAddToQuickAdd}
        />
      )}
    </div>
  );
}
